/**
 * Create a PDF from images and Office documents.
 *
 * Office files (Word, Excel, PowerPoint, and the formats they open) are
 * converted by Microsoft 365, so the PDF matches what Office itself would
 * print -- real Calibri, real page breaks. Odie puts the file for a moment in
 * the user's OneDrive (Apps/Odie conversions), asks Graph for it as PDF, and
 * deletes it. Images and PDFs then go to Odie's server, which turns pictures
 * into pages and puts everything, in the order picked, into one PDF.
 */

import { api } from '../api'
import { graphAvailable } from '../auth/msal'
import { oneDrive } from './oneDrive'

/** What Microsoft 365 converts to PDF (Graph's ``format=pdf``). */
export const OFFICE_TYPES = [
  '.doc', '.docx', '.docm', '.dot', '.dotx', '.rtf', '.odt',
  '.xls', '.xlsx', '.xlsm', '.xlsb', '.csv', '.ods',
  '.ppt', '.pptx', '.pptm', '.pps', '.ppsx', '.pot', '.potx', '.odp',
]
export const IMAGE_TYPES = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tif', '.tiff', '.webp']

/** For the file picker's ``accept``. */
export const CREATE_ACCEPT = ['.pdf', ...IMAGE_TYPES, ...OFFICE_TYPES].join(',')

const extension = (name: string) => (name.match(/\.[^.]+$/)?.[0] ?? '').toLowerCase()
export const isOffice = (file: File) => OFFICE_TYPES.includes(extension(file.name))
/** Office files and pictures, which open as a new PDF made from them. */
export const isConvertibleName = (name: string) =>
  OFFICE_TYPES.includes(extension(name)) || IMAGE_TYPES.includes(extension(name))
export const isOfficeName = (name: string) => OFFICE_TYPES.includes(extension(name))

export interface CreatedPdf {
  blob: Blob
  name: string
}

export async function createPdf(
  files: File[],
  onProgress?: (message: string) => void,
): Promise<CreatedPdf> {
  if (!files.length) throw new Error('Choose at least one file.')
  const unknown = files.filter((f) => ![...OFFICE_TYPES, ...IMAGE_TYPES, '.pdf'].includes(extension(f.name)))
  if (unknown.length) {
    throw new Error(`Odie cannot make a PDF from ${unknown.map((f) => f.name).join(', ')}.`)
  }
  if (files.some(isOffice) && !graphAvailable()) {
    throw new Error('Office files are converted by Microsoft 365, which needs sign-in (off on this server).')
  }

  const parts: File[] = []
  for (const file of files) {
    if (isOffice(file)) {
      onProgress?.(`Converting ${file.name} with Microsoft 365…`)
      const pdf = await oneDrive.convertToPdf(file)
      parts.push(new File([pdf], file.name.replace(/\.[^.]+$/, '') + '.pdf', { type: 'application/pdf' }))
    } else {
      parts.push(file)
    }
  }

  const stem = files[0].name.replace(/\.[^.]+$/, '')
  // A single PDF -- or a single Office file, now a PDF -- needs no more work.
  if (parts.length === 1 && extension(parts[0].name) === '.pdf') {
    return { blob: parts[0], name: `${stem}.pdf` }
  }
  onProgress?.(parts.length > 1 ? `Putting ${parts.length} files together…` : 'Making the PDF…')
  return { blob: await api.convert(parts), name: `${stem}.pdf` }
}
